import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    ActivityIndicator,
    Image,
    Linking,
    Modal,
    Pressable,
    StyleSheet,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { logRocketRedactProps } from "@/lib/logrocket";
import { fetchUploadHistory, type UploadHistoryEntry } from "@/lib/onboarding-api";
import { colors, fonts, radius, spacing } from "@/lib/theme";

function isPdf(entry: UploadHistoryEntry): boolean {
    if ((entry.fileType ?? "").toLowerCase().includes("pdf")) {
        return true;
    }
    return (entry.filename ?? entry.s3Url ?? "").toLowerCase().endsWith(".pdf");
}

/**
 * Full history of every document the nurse has uploaded for this onboarding —
 * one card per physical file, showing which requirement(s) it was routed to and
 * the per-item + overall status. Backed by GET /rn/:id/onboarding/uploads.
 *
 * A single file can satisfy several items (copy-across): the card lists every
 * item it landed on with that item's own status, so the nurse can see exactly
 * which requirements are covered and which still need attention.
 */
export function UploadHistory({
    rnId,
    subsectionId,
    reloadToken
}: {
    rnId: string;
    subsectionId?: string | number;
    reloadToken?: string;
}) {
    const [entries, setEntries] = useState<UploadHistoryEntry[] | null>(null);
    const [preview, setPreview] = useState<UploadHistoryEntry | null>(null);
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async () => {
        if (!rnId) {
            return;
        }
        try {
            const res = await fetchUploadHistory(rnId, subsectionId);
            setEntries(res.uploads);
        } catch {
            // Non-fatal: leave the previous list in place.
        }
    }, [rnId, subsectionId]);

    const openEntry = useCallback((entry: UploadHistoryEntry) => {
        if (!entry.viewUrl) {
            return;
        }
        // PDFs open in the system viewer/browser; images open in an in-app lightbox.
        if (isPdf(entry)) {
            void Linking.openURL(entry.viewUrl);
        } else {
            setPreview(entry);
        }
    }, []);

    // Reload on mount and whenever the parent signals a change (new upload or a
    // status update pushed over the websocket).
    useEffect(() => {
        void load();
    }, [load, reloadToken]);

    // While anything is still verifying, poll so the statuses settle without the
    // nurse having to leave and come back.
    useEffect(() => {
        const anyVerifying = entries?.some((e) => e.status === "verifying");
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        if (anyVerifying) {
            pollRef.current = setTimeout(() => void load(), 4000);
        }
        return () => {
            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [entries, load]);

    if (!entries || entries.length === 0) {
        return null;
    }

    return (
        <View style={styles.container}>
            <Text style={styles.heading}>Upload history</Text>
            <Text style={styles.subheading}>
                Every document you uploaded here, and which requirement it covered. Tap a document
                to view it.
            </Text>
            {entries.map((entry, idx) => (
                <UploadCard key={`${entry.s3Url}-${idx}`} entry={entry} onOpen={openEntry} />
            ))}

            <Modal
                visible={preview != null}
                transparent
                animationType="fade"
                onRequestClose={() => setPreview(null)}
            >
                <Pressable style={styles.lightboxBackdrop} onPress={() => setPreview(null)}>
                    {preview?.viewUrl ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: preview.viewUrl }}
                            style={styles.lightboxImage}
                            resizeMode="contain"
                        />
                    ) : null}
                    <Text style={styles.lightboxHint}>Tap anywhere to close</Text>
                </Pressable>
            </Modal>
        </View>
    );
}

function UploadCard({
    entry,
    onOpen
}: {
    entry: UploadHistoryEntry;
    onOpen: (entry: UploadHistoryEntry) => void;
}) {
    const overall = OVERALL[entry.status];
    const dateStr = entry.uploadedAt ? formatDate(entry.uploadedAt) : null;
    const canOpen = !!entry.viewUrl;

    return (
        <Pressable
            style={styles.card}
            onPress={() => onOpen(entry)}
            disabled={!canOpen}
            android_ripple={canOpen ? { color: colors.border } : undefined}
        >
            <View style={styles.cardTop}>
                {entry.viewUrl && !isPdf(entry) ? (
                    <Image
                        {...logRocketRedactProps()}
                        source={{ uri: entry.viewUrl }}
                        style={styles.thumb}
                        resizeMode="cover"
                    />
                ) : (
                    <View style={[styles.thumb, styles.thumbFallback]}>
                        <MaterialCommunityIcons
                            name={isPdf(entry) ? "file-pdf-box" : "file-document-outline"}
                            size={24}
                            color={isPdf(entry) ? colors.error500 : colors.textSecondary}
                        />
                    </View>
                )}
                <View style={styles.cardInfo}>
                    <View style={styles.filenameRow}>
                        <Text style={styles.filename} numberOfLines={1}>
                            {entry.filename ?? "Uploaded document"}
                        </Text>
                        {canOpen ? (
                            <MaterialCommunityIcons
                                name={isPdf(entry) ? "open-in-new" : "eye-outline"}
                                size={14}
                                color={colors.teal600}
                            />
                        ) : null}
                    </View>
                    {dateStr ? <Text style={styles.date}>{dateStr}</Text> : null}
                </View>
                <View style={[styles.badge, { backgroundColor: overall.bg }]}>
                    {entry.status === "verifying" ? (
                        <ActivityIndicator size="small" color={overall.fg} />
                    ) : (
                        <MaterialCommunityIcons name={overall.icon} size={14} color={overall.fg} />
                    )}
                    <Text style={[styles.badgeText, { color: overall.fg }]}>{overall.label}</Text>
                </View>
            </View>

            {/* Which requirement(s) this file covered */}
            {entry.items.length > 0 && (
                <View style={styles.itemList}>
                    {entry.items.map((item, i) => {
                        const c = itemDotColor(item.status);
                        return (
                            <View key={`${item.itemName}-${i}`} style={styles.itemRow}>
                                <View style={[styles.dot, { backgroundColor: c }]} />
                                <View style={styles.itemTextWrap}>
                                    <Text style={styles.itemName}>{item.itemName}</Text>
                                    {item.status === "rejected" && item.rejectionReason ? (
                                        <Text style={styles.itemReason}>
                                            {item.rejectionReason}
                                        </Text>
                                    ) : null}
                                </View>
                                <Text style={[styles.itemStatus, { color: c }]}>
                                    {itemStatusLabel(item.status)}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            )}

            {/* Classification-level reason (file not matched to any requirement) */}
            {entry.classificationReason ? (
                <Text style={styles.classReason}>{entry.classificationReason}</Text>
            ) : null}
        </Pressable>
    );
}

const OVERALL: Record<
    UploadHistoryEntry["status"],
    { label: string; bg: string; fg: string; icon: keyof typeof MaterialCommunityIcons.glyphMap }
> = {
    approved: {
        label: "Approved",
        bg: colors.success50,
        fg: colors.success600,
        icon: "check-circle"
    },
    rejected: { label: "Rejected", bg: colors.error50, fg: colors.error600, icon: "close-circle" },
    verifying: {
        label: "Verifying",
        bg: colors.warning50,
        fg: colors.warning600,
        icon: "progress-clock"
    },
    unrecognized: {
        label: "Not recognized",
        bg: colors.border,
        fg: colors.textSecondary,
        icon: "help-circle-outline"
    },
    pending: {
        label: "Pending",
        bg: colors.border,
        fg: colors.textSecondary,
        icon: "clock-outline"
    }
};

function itemDotColor(status: string): string {
    if (status === "approved") {
        return colors.success500;
    }
    if (status === "rejected" || status === "verification_failed") {
        return colors.error500;
    }
    if (status === "ai_verifying" || status === "submitted") {
        return colors.warning500;
    }
    return colors.textSecondary;
}

function itemStatusLabel(status: string): string {
    switch (status) {
        case "approved":
            return "Approved";
        case "rejected":
            return "Rejected";
        case "verification_failed":
            return "Failed";
        case "ai_verifying":
        case "submitted":
            return "Verifying";
        default:
            return "Pending";
    }
}

function formatDate(iso: string): string {
    // Display-only; keep it simple and locale-friendly.
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return "";
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const styles = StyleSheet.create({
    container: {
        marginTop: spacing.xl,
        gap: spacing.sm
    },
    heading: {
        fontFamily: fonts.semiBold,
        fontSize: 17,
        color: colors.ink
    },
    subheading: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        marginBottom: spacing.xs
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: spacing.base,
        gap: spacing.md
    },
    cardTop: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    thumb: {
        width: 44,
        height: 44,
        borderRadius: radius.sm,
        backgroundColor: colors.border
    },
    thumbFallback: {
        alignItems: "center",
        justifyContent: "center"
    },
    cardInfo: {
        flex: 1,
        gap: 2
    },
    filenameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    filename: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.ink,
        flexShrink: 1
    },
    date: {
        fontFamily: fonts.regular,
        fontSize: 12,
        color: colors.textSecondary
    },
    badge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: spacing.sm,
        paddingVertical: 4,
        borderRadius: radius.full
    },
    badgeText: {
        fontFamily: fonts.semiBold,
        fontSize: 12
    },
    itemList: {
        gap: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: spacing.md
    },
    itemRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4
    },
    itemTextWrap: {
        flex: 1,
        gap: 2
    },
    itemName: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.ink
    },
    itemReason: {
        fontFamily: fonts.regular,
        fontSize: 12,
        color: colors.error600
    },
    itemStatus: {
        fontFamily: fonts.semiBold,
        fontSize: 12
    },
    classReason: {
        fontFamily: fonts.regular,
        fontSize: 12,
        color: colors.error600
    },
    lightboxBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.base,
        gap: spacing.lg
    },
    lightboxImage: {
        width: "100%",
        height: "80%"
    },
    lightboxHint: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: "rgba(255, 255, 255, 0.7)"
    }
});
